import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { createServerStorage } from './serverStorage';
import { installAppCheckFetch } from './appCheck';
import { installReadOnlyFetchGuard } from './readOnlyGuard';

// Entry switch (2026-09-06): REACT_APP_PORTAL=true builds the CLIENT PORTAL (src/portal/PortalApp.jsx,
// portals.jfprofessionalsolutions.net) from this same repo; otherwise the staff billing app. The
// comparison is a compile-time constant, so webpack leaves the other app out of the bundle entirely.
let App;
if (process.env.REACT_APP_PORTAL === "true") {
  App = require("./portal/PortalApp").default; // eslint-disable-line global-require
} else {
  App = require("./App").default; // eslint-disable-line global-require
}

const ALLOW_PHI_LOCAL_CACHE = String(process.env.REACT_APP_ALLOW_PHI_LOCAL_CACHE || '').toLowerCase() === 'true';
const USE_SERVER_STORAGE = String(process.env.REACT_APP_USE_SERVER_STORAGE || '').toLowerCase() === 'true';
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const DB_NAME = 'jf_billing_app';
const DB_VER = 1;
const STORE_NAME = 'kv';

installAppCheckFetch();
installReadOnlyFetchGuard(); // no-op unless REACT_APP_READ_ONLY=true (local test builds)

function idbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

let dbPromise = null;
function getDb() {
  if (!dbPromise) dbPromise = idbOpen();
  return dbPromise;
}

let lsMigrated = false;
async function migrateJfKeysFromLocalStorage(db) {
  if (lsMigrated) return;
  lsMigrated = true;
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('jf_')) {
      const value = localStorage.getItem(key);
      try {
        await new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.objectStore(STORE_NAME).put(value, key);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
        toRemove.push(key);
      } catch (e) {
        console.warn('[jf-billing] migrate key failed:', key, e);
      }
    }
  }
  toRemove.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (_) {}
  });
}

async function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const g = tx.objectStore(STORE_NAME).get(key);
    g.onsuccess = () => resolve(g.result);
    g.onerror = () => reject(g.error);
  });
}

async function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDelete(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbListKeysWithPrefix(db, prefix) {
  const keys = [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const st = tx.objectStore(STORE_NAME);
    const req = st.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (!cur) {
        resolve(keys);
        return;
      }
      const k = cur.key;
      if (String(k).startsWith(prefix)) keys.push(k);
      cur.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

function localStorageOnlyStorage() {
  return {
    get: async (key) => {
      const value = localStorage.getItem(key);
      if (value === null) throw new Error(`Key not found: ${key}`);
      return { key, value };
    },
    set: async (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        const name = e && e.name === 'QuotaExceededError' ? 'QuotaExceededError' : (e && e.name) || 'Error';
        const msg =
          name === 'QuotaExceededError'
            ? 'Browser storage is full (about 5 MB limit for this mode). Clear site data or remove old files.'
            : (e && e.message) || String(e);
        throw new Error(msg);
      }
      return { key, value };
    },
    delete: async (key) => {
      localStorage.removeItem(key);
      return { key, deleted: true };
    },
    list: async (prefix = '') => {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith(prefix));
      return { keys };
    },
  };
}

function inMemoryStorage() {
  const cache = new Map();
  return {
    get: async (key) => {
      if (!cache.has(key)) throw new Error(`Key not found: ${key}`);
      return { key, value: cache.get(key) };
    },
    set: async (key, value) => {
      cache.set(key, value);
      return { key, value };
    },
    delete: async (key) => {
      cache.delete(key);
      return { key, deleted: true };
    },
    list: async (prefix = '') => {
      const keys = Array.from(cache.keys()).filter((k) => String(k).startsWith(prefix));
      return { keys: keys.sort() };
    },
  };
}

function indexedDbStorage() {
  return {
    get: async (key) => {
      const db = await getDb();
      await migrateJfKeysFromLocalStorage(db);
      let value = await idbGet(db, key);
      if (value === undefined) {
        const ls = localStorage.getItem(key);
        if (ls !== null) {
          try {
            await idbPut(db, key, ls);
            localStorage.removeItem(key);
            value = ls;
          } catch (e) {
            value = ls;
          }
        }
      }
      if (value === undefined) throw new Error(`Key not found: ${key}`);
      return { key, value };
    },
    set: async (key, value) => {
      const db = await getDb();
      await migrateJfKeysFromLocalStorage(db);
      try {
        localStorage.removeItem(key);
        await idbPut(db, key, value);
      } catch (e) {
        const name = e && e.name;
        const msg =
          name === 'QuotaExceededError' || (e && String(e.message || '').toLowerCase().includes('quota'))
            ? 'Storage quota exceeded. Try removing old uploaded files in the app or clear browser data for this site.'
            : (e && e.message) || String(e);
        throw new Error(msg);
      }
      return { key, value };
    },
    delete: async (key) => {
      const db = await getDb();
      await migrateJfKeysFromLocalStorage(db);
      try {
        localStorage.removeItem(key);
      } catch (_) {}
      await idbDelete(db, key);
      return { key, deleted: true };
    },
    list: async (prefix = '') => {
      const db = await getDb();
      await migrateJfKeysFromLocalStorage(db);
      const fromIdb = await idbListKeysWithPrefix(db, prefix);
      const fromLs = Object.keys(localStorage).filter((k) => k.startsWith(prefix));
      const merged = [...new Set([...fromIdb, ...fromLs])];
      return { keys: merged.sort() };
    },
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));

async function boot() {
  let storageImpl = inMemoryStorage();
  if (USE_SERVER_STORAGE) {
    storageImpl = createServerStorage(API_BASE);
  } else if (ALLOW_PHI_LOCAL_CACHE) {
    storageImpl = localStorageOnlyStorage();
    if (typeof indexedDB !== 'undefined') {
      try {
        await idbOpen();
        storageImpl = indexedDbStorage();
      } catch (e) {
        console.warn('[jf-billing] IndexedDB unavailable, using localStorage:', e);
      }
    }
  } else {
    console.warn(
      '[jf-billing] Persistent browser caching is disabled by default for HIPAA risk reduction. ' +
      'Set REACT_APP_ALLOW_PHI_LOCAL_CACHE=true only after formal risk acceptance.'
    );
  }
  window.storage = storageImpl;
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

boot();
